import { collection, doc, runTransaction } from "firebase/firestore";

const getActorName = (user = {}) => user?.displayName || user?.name || "Unknown";
const getActorEmail = (user = {}) => user?.email || "";

const buildHistoryEntry = ({
  status,
  user,
  timestamp,
  isCreate,
  action = isCreate ? "created" : "updated",
  remarks = ""
}) => ({
  createdAt: isCreate ? timestamp : "",
  createdBy: isCreate ? getActorName(user) : "",
  createdByEmail: isCreate ? getActorEmail(user) : "",
  updatedAt: timestamp,
  updatedBy: getActorName(user),
  updatedByEmail: getActorEmail(user),
  orderStatus: status || "",
  action,
  actionAt: timestamp,
  actionBy: getActorName(user),
  actionByEmail: getActorEmail(user),
  remarks
});

export const createOrderWithAudit = async (db, { orderPayload, user, orderId, historyEvent = null }) => {
  const timestamp = orderPayload.createdAt || new Date().toISOString();
  const nextOrderId = orderId || doc(collection(db, "orders")).id;
  const orderRef = doc(db, "orders", nextOrderId);
  const auditRef = doc(db, "audit_events", nextOrderId);
  const actorName = getActorName(user);
  const actorEmail = getActorEmail(user);
  const historyEntries = [
    buildHistoryEntry({
      status: orderPayload.status,
      user,
      timestamp,
      isCreate: true
    })
  ];

  if (historyEvent && historyEvent.action !== 'created') {
    historyEntries.push(
      buildHistoryEntry({
        status: historyEvent.status || orderPayload.status,
        user: historyEvent.user || user,
        timestamp: historyEvent.timestamp || timestamp,
        isCreate: false,
        action: historyEvent.action,
        remarks: historyEvent.remarks || ""
      })
    );
  }

  await runTransaction(db, async (transaction) => {
    transaction.set(orderRef, orderPayload);
    transaction.set(auditRef, {
      createdAt: timestamp,
      createdBy: actorName,
      createdByEmail: actorEmail,
      updatedAt: orderPayload.updatedAt || timestamp,
      updatedBy: orderPayload.updatedBy || actorName,
      updatedByEmail: orderPayload.updatedByEmail || actorEmail,
      orderId: nextOrderId,
      orderStatus: orderPayload.status || "",
      history: historyEntries
    });
  });

  return nextOrderId;
};

export const updateOrderWithAudit = async (
  db,
  {
    orderId,
    orderPayload,
    previousData = {},
    user,
    historyEvent = null,
    auditFields = {}
  }
) => {
  const orderRef = doc(db, "orders", orderId);
  const auditRef = doc(db, "audit_events", orderId);
  const timestamp = orderPayload.updatedAt || new Date().toISOString();

  await runTransaction(db, async (transaction) => {
    const auditSnap = await transaction.get(auditRef);
    const existingAudit = auditSnap.exists() ? auditSnap.data() : {};
    const history = Array.isArray(existingAudit.history) ? existingAudit.history : [];

    transaction.update(orderRef, orderPayload);
    transaction.set(
      auditRef,
      {
        createdAt: existingAudit.createdAt || previousData.createdAt || timestamp,
        createdBy: existingAudit.createdBy || previousData.createdBy || "",
        createdByEmail: existingAudit.createdByEmail || previousData.createdByEmail || "",
        updatedAt: timestamp,
        updatedBy: orderPayload.updatedBy || getActorName(user),
        updatedByEmail: orderPayload.updatedByEmail || getActorEmail(user),
        orderId,
        orderStatus: orderPayload.status || previousData.status || "",
        ...auditFields,
        history: [
          ...history,
          buildHistoryEntry({
            status: historyEvent?.status || orderPayload.status || previousData.status,
            user: historyEvent?.user || user,
            timestamp: historyEvent?.timestamp || timestamp,
            isCreate: false,
            action: historyEvent?.action || "updated",
            remarks: historyEvent?.remarks || ""
          })
        ]
      },
      { merge: true }
    );
  });
};
